import { Notification } from "components";
import { EnumEmployeeDelegationMode } from "constants/enums/employee-delegation-mode";
import { compose, withFormik, withHooks, withTranslation } from "enhancers";
import withPreventLeaveDirtyForm from "enhancers/withPreventLeaveDirtyForm";
import { PageContent } from "layouts";
import { gql, homePath, paths, Yup } from "utils/helper";
import { EMPLOYEE_DELEGATION_API } from "..";
import { EmployeeDelegationForm } from "../components/EmployeeDelegationForm";

export interface MouSettingFormData {
  delegateId?: string;
  mode: EnumEmployeeDelegationMode;
  targets?: string[];
}

export const defaultValues: MouSettingFormData = {
  delegateId: "",
  mode: EnumEmployeeDelegationMode.CHOOSE,
};

export const validationSchema = Yup.object().shape({
  delegateId: Yup.string().nullable().required("ต้องไม่เว้นว่างไว้"),
  mode: Yup.string().required("ต้องไม่เว้นว่างไว้"),
});

// GraphQL API definitions
const API = {
  CREATE_EMPLOYEE_DELEGATION: gql`
    mutation CREATE_EMPLOYEE_DELEGATION(
      $delegateId: String!
      $mode: String!
      $targets: [String!]
    ) {
      createEmployeeDelegation(
        input: { delegateId: $delegateId, mode: $mode, targets: $targets }
      ) {
        id
      }
    }
  `,
};

const EmployeeDelegationDetailPage = ({
  title,
  breadcrumbs,
  loading,
  isAllMode,
  selectedDelegateId,
  isSubmitting,
}: any) => {
  return (
    <PageContent title={title} breadcrumbs={breadcrumbs} loading={loading}>
      <EmployeeDelegationForm
        isAllMode={isAllMode}
        selectedDelegateId={selectedDelegateId}
        isSubmitting={isSubmitting}
      />
    </PageContent>
  );
};

const enhancer = compose(
  withFormik({
    mapPropsToValues: () => defaultValues,
    validationSchema: Yup.object().shape({
      delegateId: Yup.string().nullable().required(".required"),
      mode: Yup.string().required(".required"),
    }),
  }),
  withPreventLeaveDirtyForm(),
  withTranslation({ prefix: "pages.main.employeeDelegation.create" }),
  withHooks((props: any, hooks: any) => {
    const {
      t,
      values,

      setFieldValue,
      isSubmitting,
    } = props;
    const {
      useMemo,

      useHandleSubmit,

      useMutation,
      useEffect,
    } = hooks;

    const [createEmployeeDelegation] = useMutation(
      API.CREATE_EMPLOYEE_DELEGATION,
      {
        refetchQueries: [
          {
            query: EMPLOYEE_DELEGATION_API.FETCH_EMPLOYEE_DELEGATIONS,
          },
        ],
        onCompleted: () => {
          Notification.success(t(".success"));
          paths.employeeDelegationPath().push();
        },
      }
    );

    const title = t(".title");

    const breadcrumbs = useMemo(
      () => [
        { path: homePath(), label: t(".home") },
        {
          path: paths.employeeDelegationPath(),
          label: t(".employeeDelegation"),
        },
        { path: null, label: title },
      ],
      [title, t]
    );

    useHandleSubmit(async (values: any) => {
      await createEmployeeDelegation({
        variables: {
          delegateId: values.delegateId,
          mode: values.mode,
          targets: values.targets,
        },
      });
    });

    const isAllMode = useMemo(() => {
      return values?.mode === EnumEmployeeDelegationMode.ALL;
    }, [values]);

    const selectedDelegateId = useMemo(() => {
      return values?.delegateId;
    }, [values?.delegateId]);

    useEffect(() => {
      if (
        values.mode === EnumEmployeeDelegationMode.ALL &&
        values.targets?.length > 0
      ) {
        setFieldValue("targets", []);
      }
    }, [values, setFieldValue]);

    return {
      title,
      breadcrumbs,
      isAllMode,
      selectedDelegateId,
      isSubmitting,
    };
  })
);

export default enhancer(EmployeeDelegationDetailPage);
